/**
 * Utility functions for phone number detection and validation
 */

/**
 * Checks if the input contains only numeric characters (and optional formatting)
 * @param input - The input string to check
 * @returns Whether the input appears to be a phone number
 */
export const isPhoneNumber = (input: string): boolean => {
  if (!input || input.length < 3) {
    return false;
  }

  // Remove common phone number formatting characters
  // eslint-disable-next-line no-useless-escape
  const cleaned = input.replace(/[\s\-\(\)\+\.]/g, '');
  // Check if it contains only digits
  const isOnlyDigits = /^\d+$/.test(cleaned);
  const isValidLength = cleaned.length >= 3;
  const result = isOnlyDigits && isValidLength;

  return result;
};

/**
 * Extracts numeric digits from a phone number string
 * @param input - The input string
 * @returns Clean numeric string
 */
export const cleanPhoneNumber = (input: string): string => {
  const result = input.replace(/[^\d]/g, '');
  return result;
};

/**
 * Checks if a phone number matches an existing contact option
 * Enhanced to check both the main value and additional phone numbers
 * @param phoneNumber - Phone number to check
 * @param existingOptions - Array of existing contact options
 * @returns Whether the number is already selected
 */
interface ContactWithPhones {
  value?: string;
  phoneNumbers?: { number?: string }[];
}

export const isPhoneNumberAlreadySelected = (
  phoneNumber: string,
  existingOptions: ContactWithPhones[]
): boolean => {
  const cleanedInput = cleanPhoneNumber(phoneNumber);

  const result = existingOptions.some((option) => {
    // Check the main value
    const cleanedOption = cleanPhoneNumber(option.value || '');
    if (cleanedOption === cleanedInput) {
      return true;
    }

    // Check additional phone numbers if available
    if (option.phoneNumbers && Array.isArray(option.phoneNumbers)) {
      return option.phoneNumbers.some((phone) => {
        const cleanedPhone = cleanPhoneNumber(phone.number || '');
        return cleanedPhone === cleanedInput;
      });
    }

    return false;
  });

  return result;
};
